import ProntoStorage from '@/helpers/storage'
import DexieWorker from './dexie-worker?worker&inline'

const storage = new ProntoStorage('localStorage')
const sStorage = new ProntoStorage('sessionStorage')

let jobId = 0
const jobRegister = {}

const handleMessage = (e) => {
	const { id, type, data } = e.data
	if (type.endsWith('Error')) return handleError(id, e.data)
	const job = jobRegister[id]
	if (type === 'unknown') {
		// eslint-disable-next-line no-console
		const logger = console.orig_error || console.error
		logger?.('UNKNOWN DB JOB', job)
	} else if (type === 'log') {
		// eslint-disable-next-line no-console
		const logger = console.orig_debug || console.debug
		logger?.('LOG EVENT', id, data)
	}
	if (!job?.resolve) {
		// eslint-disable-next-line no-console
		const logger = console.orig_log || console.log
		logger?.('UNKNOWN DB ERROR', id, type)
		return
	}
	job.success = true
	job.resolve(data)
	cleanJob(job)
}

const worker = new DexieWorker()
worker.addEventListener('message', handleMessage)

// rejection reasons must be real Errors or Bugsnag reports them as
// "unhandledrejection handler received a non-error"
const toError = (e, job) => {
	if (e instanceof Error) return e
	// worker catch payloads ({ type: 'putError', ..., error }) where error is
	// the serialized { name, message, stack } posted by the worker
	const src = e?.error
	if (src instanceof Error) return src
	if (typeof src?.message === 'string' && src.message) {
		const err = new Error(src.message)
		if (src.name) err.name = src.name
		if (src.stack) err.stack = src.stack
		return err
	}
	// worker 'error' ErrorEvent
	if (typeof e?.message === 'string' && e.message) return new Error(e.message)
	const err = new Error(`DB job failed: ${e?.type || job?.type || 'unknown'}`)
	err.detail = e
	return err
}

const handleError = (id, e) => {
	const job = jobRegister[id]
	// eslint-disable-next-line no-console
	const logger = console.orig_error || console.error
	logger?.('FAILED DB JOB', id, job, e)
	if (!job) return
	job.error = true
	job.reject(toError(e, job))
	cleanJob(job)
}

const cleanJob = (job) => {
	if (!job) return job
	job.time = Date.now() - job.time
	worker.removeEventListener('error', job.onError)
	delete jobRegister[job.id]
}

const setupJobPromise = (type, data) => {
	return new Promise((resolve, reject) => {
		if (!type) return reject(new Error('No postMessage payload'))
		const id = jobId++
		const onError = (e) => handleError(id, e)
		jobRegister[id] = {
			id,
			type,
			data,
			resolve,
			reject,
			onError,
			time: Date.now(),
		}
		worker.addEventListener('error', onError)
		if (data) {
			// TODO: figure out how to get around these Proxy errors
			// DataCloneError: Failed to execute 'postMessage' on 'Worker': #<Object> could not be cloned.
			try {
				data = JSON.parse(JSON.stringify(data))
			} catch (e) {
				data = '[SerializationError]'
			}
		}
		worker.postMessage({
			id,
			type,
			data,
		})
	})
}

export const setUserKey = (userKey) => {
	worker.postMessage({ type: 'userKey', data: userKey })
}

export const setEncryption = (shouldEncrypt) => {
	worker.postMessage({
		type: 'shouldEncrypt',
		data: shouldEncrypt,
	})
}

export const setupDB = (userKey, persist) => {
	userKey = userKey ?? sStorage.get('userKey') ?? storage.get('userKey')
	persist = persist ?? (storage.get('persist') || false)

	if (!userKey) {
		clearAllTables()
	}

	setUserKey(userKey)
	setEncryption(!persist)
}

export const dbBulkPut = async (tableName, items) => {
	return setupJobPromise('bulkPut', { tableName, items })
}

export const dbPut = async (tableName, item) => {
	return setupJobPromise('put', { tableName, item })
}

export const importTable = async (tableName, clauses = {}) => {
	return setupJobPromise('importTable', { tableName, ...clauses })
}

export const importTables = async (tableNames) => {
	return setupJobPromise('importTables', { tableNames })
}

export const deleteFromDB = async (tableName, where) => {
	return setupJobPromise('deleteFrom', { tableName, where })
}

export const deleteKeyFromDB = async (tableName, primaryKey) => {
	return setupJobPromise('deleteKey', { tableName, primaryKey })
}

export const deleteEntireDB = async () => {
	return setupJobPromise('deleteDatabase')
}

export const clearTable = async (tableName) => {
	return setupJobPromise('clearTable', { tableName })
}

export const clearAllTables = async () => {
	return setupJobPromise('clearAllTables')
}

export const countTableRows = async (tableName) => {
	return setupJobPromise('countTableRows', { tableName })
}

export const deleteTableRows = async (tableName, clauses = {}) => {
	return setupJobPromise('deleteTableRows', { tableName, ...clauses })
}

export const dumpLogsToFile = async (tableName, data = {}) => {
	return setupJobPromise('dumpLogsToFile', { tableName, ...data })
}

export const deleteFile = async (fileName) => {
	return setupJobPromise('deleteFile', { fileName })
}

export const getFileBlob = async (fileName, type) => {
	return setupJobPromise('getFileBlob', { fileName, type })
}

export const writeToLog = async (lines) => {
	return setupJobPromise('writeToLog', { lines })
}

export const getLogFileBlob = async () => {
	return setupJobPromise('getLogFileBlob')
}

export const clearLogFile = async () => {
	return setupJobPromise('clearLogFile')
}

setupDB()
